/**
 * LERA Variant Media Sync — Sprint 8.3C.3
 * Synchronizes LERA / Dawn media-gallery with the selected variant.
 * Listens to LERA variant:selected and Dawn variantChange. No gallery replacement.
 */

(function () {
  const HIDDEN_CLASS = 'lera-media-sync--hidden';
  const boundRoots = new WeakSet();

  function readConfig(productInfo) {
    try {
      const node = productInfo.querySelector('[data-lera-media-sync-config]');
      if (!node) return null;
      const config = JSON.parse(node.textContent);
      if (config.variantFeaturedMediaList && !config.variantFeaturedMedia) {
        config.variantFeaturedMedia = Object.fromEntries(
          config.variantFeaturedMediaList
            .filter((entry) => entry?.variantId != null)
            .map((entry) => [String(entry.variantId), entry.mediaId ?? null])
        );
      }
      config.productId = config.productId || productInfo.dataset.productId || null;
      return config;
    } catch {
      return null;
    }
  }

  function mediaKey(sectionId, mediaId) {
    return `${sectionId}-${mediaId}`;
  }

  function normalizeSrc(src) {
    if (!src) return '';
    if (typeof src === 'string') return src;
    if (typeof src === 'object' && src.src) return src.src;
    return String(src);
  }

  function buildMediaVariantOwners(config) {
    const owners = new Map();
    for (const entry of config.variantFeaturedMediaList || []) {
      if (!entry.mediaId) continue;
      const list = owners.get(entry.mediaId) || [];
      list.push(String(entry.variantId));
      owners.set(entry.mediaId, list);
    }
    return owners;
  }

  function isMediaVisibleForVariant(mediaId, mediaSrc, variantId, config, owners) {
    const variantKey = String(variantId);
    const ownerList = owners.get(mediaId);

    if (ownerList?.length) {
      if (ownerList.includes(variantKey)) return true;
      if (ownerList.length > 1) return true;
      return false;
    }

    if (!config.hideVariants) return true;

    const variantSrcSet = new Set((config.variantImageSrcs || []).map(normalizeSrc));
    return !variantSrcSet.has(normalizeSrc(mediaSrc));
  }

  function getFeaturedMediaId(variant, config) {
    if (!variant) return null;

    const mapped = config.variantFeaturedMedia?.[String(variant.id)];
    if (mapped) return mapped;

    const listEntry = config.variantFeaturedMediaList?.find(
      (entry) => String(entry.variantId) === String(variant.id)
    );
    if (listEntry?.mediaId) return listEntry.mediaId;
    if (listEntry?.featuredSrc) {
      const bySrc = (config.media || []).find((item) => item.src === listEntry.featuredSrc);
      if (bySrc) return bySrc.id;
    }

    const featured = variant.featured_media || variant.featured_image;
    if (featured?.id) {
      const byId = (config.media || []).find((item) => item.id === featured.id);
      if (byId) return byId.id;
    }

    if (featured?.src) {
      const bySrc = (config.media || []).find((item) => item.src === featured.src);
      if (bySrc) return bySrc.id;
    }

    return null;
  }

  function resolveVisibleMediaIds(variant, config) {
    const featuredId = getFeaturedMediaId(variant, config);
    const owners = buildMediaVariantOwners(config);
    const ids = [];
    const seen = new Set();

    const add = (id) => {
      if (id == null || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    };

    add(featuredId);

    for (const item of config.media || []) {
      if (item.id === featuredId) continue;
      if (!isMediaVisibleForVariant(item.id, item.src, variant.id, config, owners)) continue;
      add(item.id);
    }

    if (!ids.length && config.media?.length) {
      add(config.media[0].id);
    }

    return ids;
  }

  function getGalleryElements(productInfo, sectionId) {
    const mediaGallery = productInfo.querySelector('media-gallery');
    if (!mediaGallery) return null;

    return {
      mediaGallery,
      viewerList: mediaGallery.querySelector(`#Slider-Gallery-${sectionId}`),
      thumbList: mediaGallery.querySelector(`#Slider-Thumbnails-${sectionId}`),
    };
  }

  function setNodeHidden(node, hidden) {
    node.classList.toggle(HIDDEN_CLASS, hidden);
    node.toggleAttribute('hidden', hidden);
    node.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  function reorderAndFilterList(list, itemSelector, keyFromNode, visibleKeys) {
    if (!list) return;

    const visibleSet = new Set(visibleKeys);
    const nodeMap = new Map();

    list.querySelectorAll(itemSelector).forEach((node) => {
      const key = keyFromNode(node);
      nodeMap.set(key, node);
      setNodeHidden(node, !visibleSet.has(key));
    });

    visibleKeys.forEach((key) => {
      const node = nodeMap.get(key);
      if (node) list.appendChild(node);
    });
  }

  function activateMedia(gallery, sectionId, mediaId, visibleIds) {
    if (!gallery?.mediaGallery) return;

    const targetMediaId = mediaId ?? visibleIds?.[0] ?? null;
    if (!targetMediaId) return;

    const key = mediaKey(sectionId, targetMediaId);
    const viewer = gallery.mediaGallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery.mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    const activeSlide = viewer?.querySelector(`[data-media-id="${key}"]`);
    if (!activeSlide) return;

    viewer.querySelectorAll('[data-media-id]').forEach((node) => node.classList.remove('is-active'));
    activeSlide.classList.add('is-active');

    if (activeSlide.parentElement.firstElementChild !== activeSlide) {
      activeSlide.parentElement.prepend(activeSlide);
    }

    if (thumbnails) {
      const activeThumb = thumbnails.querySelector(`[data-target="${key}"]`);
      if (activeThumb && activeThumb.parentElement.firstElementChild !== activeThumb) {
        activeThumb.parentElement.prepend(activeThumb);
      }
      thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
      activeThumb?.querySelector('button')?.setAttribute('aria-current', 'true');
      if (activeThumb && thumbnails.slider?.scrollTo) {
        thumbnails.scrollTo({ left: activeThumb.offsetLeft, behavior: 'auto' });
      }
    }

    if (viewer?.resetPages) {
      viewer.resetPages();
    } else if (viewer?.slider?.resetPages) {
      viewer.slider.resetPages();
    }

    activeSlide.parentElement.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });

    window.pauseAllMedia?.();
    activeSlide.querySelector('.deferred-media')?.loadContent?.(false);
  }

  function mergeGalleryListFromHtml(liveList, htmlList, datasetKey) {
    if (!liveList || !htmlList) return;

    const attr = datasetKey === 'target' ? 'data-target' : 'data-media-id';
    const getKey = (node) => node.getAttribute(attr);

    const refresh = () => {
      const liveItems = Array.from(liveList.querySelectorAll(`li[${attr}]`));
      const liveSet = new Set(liveItems.map(getKey));
      const liveMap = new Map(liveItems.map((item, index) => [getKey(item), { item, index }]));
      return { liveItems, liveSet, liveMap };
    };

    let { liveItems, liveSet, liveMap } = refresh();
    const htmlItems = Array.from(htmlList.querySelectorAll(`li[${attr}]`));
    const htmlSet = new Set(htmlItems.map(getKey));

    for (let i = htmlItems.length - 1; i >= 0; i--) {
      const key = getKey(htmlItems[i]);
      if (!liveSet.has(key)) {
        liveList.prepend(htmlItems[i].cloneNode(true));
      }
    }

    ({ liveItems, liveSet, liveMap } = refresh());

    for (let i = liveItems.length - 1; i >= 0; i--) {
      if (!htmlSet.has(getKey(liveItems[i]))) {
        liveItems[i].remove();
      }
    }

    ({ liveItems, liveSet, liveMap } = refresh());

    htmlItems.forEach((htmlItem, htmlIndex) => {
      const key = getKey(htmlItem);
      const liveData = liveMap.get(key);
      if (liveData && liveData.index !== htmlIndex) {
        liveList.insertBefore(liveData.item, liveList.querySelector(`li:nth-of-type(${htmlIndex + 1})`));
        ({ liveItems, liveSet, liveMap } = refresh());
      }
    });
  }

  function syncGalleryFromFetchedHtml(productInfo, config, variant, htmlRoot) {
    const gallery = getGalleryElements(productInfo, config.sectionId);
    const fetchedGallery = htmlRoot?.querySelector?.('media-gallery');
    if (!gallery?.viewerList || !fetchedGallery) {
      syncVariantMedia(productInfo, config, variant);
      return;
    }

    const fetchedViewer = fetchedGallery.querySelector(`#Slider-Gallery-${config.sectionId}`);
    const fetchedThumbs = fetchedGallery.querySelector(`#Slider-Thumbnails-${config.sectionId}`);

    mergeGalleryListFromHtml(gallery.viewerList, fetchedViewer, 'mediaId');
    mergeGalleryListFromHtml(gallery.thumbList, fetchedThumbs, 'target');

    syncVariantMedia(productInfo, config, variant);
  }

  function syncVariantMedia(productInfo, config, variant) {
    if (!variant || !config) return;

    const gallery = getGalleryElements(productInfo, config.sectionId);
    if (!gallery?.viewerList) return;

    const visibleIds = resolveVisibleMediaIds(variant, config);
    const visibleKeys = visibleIds.map((id) => mediaKey(config.sectionId, id));

    reorderAndFilterList(
      gallery.viewerList,
      'li[data-media-id]',
      (node) => node.dataset.mediaId,
      visibleKeys
    );

    reorderAndFilterList(
      gallery.thumbList,
      'li[data-target]',
      (node) => node.dataset.target,
      visibleKeys
    );

    activateMedia(gallery, config.sectionId, visibleIds[0] ?? null, visibleIds);
  }

  function bindProductInfo(productInfo) {
    if (!productInfo || boundRoots.has(productInfo)) return;
    const config = readConfig(productInfo);
    if (!config) return;

    boundRoots.add(productInfo);

    let pendingFrame = null;
    const scheduleSync = (variant) => {
      if (!variant) return;
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        syncVariantMedia(productInfo, config, variant);
      });
    };

    if (window.LeraSmartSelector?.onVariantSelected) {
      window.LeraSmartSelector.onVariantSelected((detail) => {
        if (detail?.product?.id && String(detail.product.id) !== String(config.productId)) return;
        scheduleSync(detail.variant);
      });
    }

    document.addEventListener('lera:variant:selected', (event) => {
      const detail = event.detail;
      if (detail?.product?.id && String(detail.product.id) !== String(config.productId)) return;
      scheduleSync(detail?.variant);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event?.data;
        if (!data || String(data.sectionId) !== String(config.sectionId)) return;
        syncGalleryFromFetchedHtml(productInfo, config, data.variant, data.html);
      });
    }
  }

  function boot() {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      bindProductInfo(productInfo);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);

  window.LeraVariantMediaSync = {
    syncVariantMedia,
    syncGalleryFromFetchedHtml,
    resolveVisibleMediaIds,
    readConfig,
  };
})();
